import * as React from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { Search } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { BackLink } from "@/components/back-link";
import { DataTable } from "@/components/data-table";
import {
  EmptyState,
  ErrorState,
  KeyValues,
  LoadingRows,
  Mono,
  PageHeader,
  StatusBadge,
} from "@/components/common";
import { api } from "@/lib/api";
import { dateTime, shortId } from "@/lib/format";
import { label } from "@/lib/labels";

interface UserRow {
  id: string;
  email: string | null;
  name: string | null;
  isGuest: boolean;
  paymentBlocked: boolean;
  deleted: boolean;
  createdAt: string;
}

function Flags({ u }: { u: Pick<UserRow, "isGuest" | "paymentBlocked" | "deleted"> }) {
  return (
    <span className="flex flex-wrap gap-1">
      {u.isGuest && <Badge variant="secondary">Misafir</Badge>}
      {u.paymentBlocked && <Badge variant="destructive">Ödeme engeli</Badge>}
      {u.deleted && <Badge variant="outline">Silinmiş</Badge>}
    </span>
  );
}

const columns: ColumnDef<UserRow, unknown>[] = [
  {
    accessorKey: "name",
    header: "Ad",
    cell: ({ row }) => row.original.name ?? <span className="text-muted-foreground">—</span>,
  },
  { accessorKey: "email", header: "E-posta", cell: ({ row }) => row.original.email ?? "—" },
  { id: "flags", header: "", cell: ({ row }) => <Flags u={row.original} /> },
  {
    accessorKey: "createdAt",
    header: "Kayıt",
    cell: ({ row }) => dateTime(row.original.createdAt),
  },
];

function useDebounced<T>(value: T, ms = 300) {
  const [v, setV] = React.useState(value);
  React.useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function UsersPage() {
  const [params, setParams] = useSearchParams();
  const [text, setText] = React.useState(params.get("q") ?? "");
  const term = useDebounced(text.trim());
  const navigate = useNavigate();
  React.useEffect(() => {
    setParams(term ? { q: term } : {}, { replace: true });
  }, [term, setParams]);
  const valid = term.length === 0 || term.length >= 2;
  const q = useQuery({
    queryKey: ["admin-users", term],
    queryFn: () =>
      api<{ users: UserRow[] }>(`/v1/admin/users?${new URLSearchParams(term ? { q: term } : {})}`),
    enabled: valid,
  });
  return (
    <>
      <PageHeader
        title="Kullanıcılar"
        description="E-posta, ad ya da kullanıcı kimliğiyle arayın."
      />
      <div className="relative mb-4 max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          className="pl-9"
          placeholder="ör. maria@example.com"
          value={text}
          onChange={(e) => setText(e.target.value)}
          aria-label="Kullanıcı ara"
        />
      </div>
      <DataTable
        columns={columns}
        data={q.data?.users}
        isLoading={valid && q.isPending}
        error={q.error}
        onRowClick={(u) => navigate(`/users/${u.id}`)}
        empty={valid ? "Eşleşen kullanıcı yok." : "En az 2 karakter yazın."}
      />
    </>
  );
}

interface UserDetail {
  id: string;
  email: string | null;
  phone: string | null;
  name: string | null;
  locale: string | null;
  isGuest: boolean;
  paymentBlockedAt: string | null;
  deletedAt: string | null;
  createdAt: string;
  providers: { id: string; displayName: string; status: string; role: string }[];
  recentTasks: { id: string; status: string; scheduledStart: string | null; createdAt: string }[];
  cards: {
    brand: string | null;
    last4: string | null;
    expMonth: number | null;
    expYear: number | null;
    isDefault: boolean | null;
  }[];
  failedPayments: number;
}

export function UserDetailPage() {
  const { id = "" } = useParams();
  const q = useQuery({
    queryKey: ["admin-user", id],
    queryFn: () => api<UserDetail>(`/v1/admin/users/${id}`),
  });
  const back = <BackLink to="/users">Kullanıcılar</BackLink>;
  if (q.isPending)
    return (
      <>
        {back}
        <LoadingRows rows={6} />
      </>
    );
  if (q.error)
    return (
      <>
        {back}
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      </>
    );
  const u = q.data;
  return (
    <>
      <PageHeader
        back={back}
        title={u.name ?? u.email ?? "Adsız kullanıcı"}
        description={
          <span className="flex items-center gap-2">
            <Mono>{u.id}</Mono>
            <Flags
              u={{
                isGuest: u.isGuest,
                paymentBlocked: Boolean(u.paymentBlockedAt),
                deleted: Boolean(u.deletedAt),
              }}
            />
          </span>
        }
      />
      <p className="mb-4 text-xs text-muted-foreground">
        Bu sayfanın görüntülenmesi denetim kaydına yazıldı (K-092).
      </p>
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">İletişim</CardTitle>
          </CardHeader>
          <CardContent>
            <KeyValues
              items={[
                ["E-posta", u.email],
                ["Telefon", u.phone],
                ["Dil", u.locale],
                ["Kayıt", dateTime(u.createdAt)],
                ["Ödeme engeli", u.paymentBlockedAt ? dateTime(u.paymentBlockedAt) : "Yok"],
                ["Başarısız ödeme", u.failedPayments],
                ["Silinme", u.deletedAt ? dateTime(u.deletedAt) : "—"],
              ]}
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Kartlar ve hizmet veren hesapları</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4">
            {u.cards.length ? (
              <ul className="grid gap-1 text-sm">
                {u.cards.map((c, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <span className="capitalize">{c.brand ?? "kart"}</span> •••• {c.last4}
                    <span className="text-muted-foreground">
                      {c.expMonth}/{c.expYear}
                    </span>
                    {c.isDefault && <Badge variant="secondary">Varsayılan</Badge>}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">Kayıtlı kart yok.</p>
            )}
            {u.providers.length > 0 && (
              <ul className="grid gap-1 text-sm">
                {u.providers.map((p) => (
                  <li key={p.id} className="flex items-center gap-2">
                    <Link className="text-primary hover:underline" to={`/providers/${p.id}`}>
                      {p.displayName}
                    </Link>
                    <StatusBadge status={p.status} />
                    <span className="text-muted-foreground">{label(p.role)}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
        <Card className="lg:col-span-2">
          <CardHeader className="flex-row items-center justify-between">
            <CardTitle className="text-base">Son işler</CardTitle>
            <Link className="text-sm text-primary hover:underline" to={`/tasks?customer=${u.id}`}>
              Tümü
            </Link>
          </CardHeader>
          <CardContent>
            {u.recentTasks.length ? (
              <ul className="divide-y text-sm">
                {u.recentTasks.map((t) => (
                  <li key={t.id} className="flex items-center justify-between py-2">
                    <Link className="flex items-center gap-2 hover:underline" to={`/tasks/${t.id}`}>
                      <Mono>{shortId(t.id)}</Mono> <StatusBadge status={t.status} />
                    </Link>
                    <span className="text-muted-foreground">
                      {dateTime(t.scheduledStart ?? t.createdAt)}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState>İş yok.</EmptyState>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
